import React from 'react';
import { Link } from 'react-router-dom';
import {
	Alert,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Paper,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import type { RequestRow } from './ManualVc';

type Action = 'approve' | 'reject' | 'resolve' | '';
type Props = {
	open: boolean;
	request?: RequestRow;
	busy: boolean;
	error: string;
	notice: string;
	canReview: boolean;
	canResolve: boolean;
	canViewInvoice: boolean;
	canPrevious: boolean;
	canNext: boolean;
	onPrevious(): void;
	onNext(): void;
	onClose(): void;
	action: Action;
	note: string;
	onAction(value: Action): void;
	onNote(value: string): void;
	onConfirm(): void;
	proof?: React.ReactNode;
};
const date = (value?: string) =>
	value ? new Date(value).toLocaleString() : '—';

export default function ManualVcReviewDialog({
	open,
	request: r,
	busy,
	error,
	notice,
	canReview,
	canResolve,
	canViewInvoice,
	canPrevious,
	canNext,
	onPrevious,
	onNext,
	onClose,
	action,
	note,
	onAction,
	onNote,
	onConfirm,
	proof,
}: Props) {
	return (
		<Dialog
			open={open}
			onClose={busy ? undefined : onClose}
			maxWidth="lg"
			fullWidth
			aria-labelledby="manual-vc-review-title"
			PaperProps={{
				sx: {
					height: 'min(780px, calc(100dvh - 48px))',
					maxHeight: 'calc(100dvh - 48px)',
					m: { xs: 1.5, sm: 3 },
					width: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 48px)' },
				},
			}}
		>
			<DialogTitle
				id="manual-vc-review-title"
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 1,
					py: 1.5,
				}}
			>
				<IconButton
					aria-label="Previous request"
					title="Previous request"
					disabled={busy || !r || !canPrevious}
					onClick={onPrevious}
				>
					<ChevronLeft />
				</IconButton>
				<Typography component="span" variant="h6">
					Manual VC Request
				</Typography>
				<IconButton
					aria-label="Next request"
					title="Next request"
					disabled={busy || !r || !canNext}
					onClick={onNext}
				>
					<ChevronRight />
				</IconButton>
			</DialogTitle>
			{error && (
				<Alert severity="error" sx={{ mx: 2, mb: 1 }}>
					{error}
				</Alert>
			)}
			{notice && (
				<Alert severity="success" sx={{ mx: 2, mb: 1 }}>
					{notice}
				</Alert>
			)}
			<DialogContent dividers sx={{ p: 0, minHeight: 0, display: 'flex' }}>
				{!r ? (
					<Typography role="status" sx={{ p: 3 }}>
						Loading request…
					</Typography>
				) : (
					<Box
						sx={{
							display: 'grid',
							width: '100%',
							minHeight: 0,
							gridTemplateColumns: {
								xs: 'minmax(0, 1fr)',
								md: 'minmax(0, .9fr) minmax(0, 1.1fr)',
							},
							gridTemplateRows: { xs: 'auto 360px', md: 'minmax(0, 1fr)' },
							overflowY: { xs: 'auto', md: 'hidden' },
						}}
					>
						<Box
							sx={{
								p: { xs: 2, md: 3 },
								minWidth: 0,
								overflowY: { md: 'auto' },
								overflowWrap: 'anywhere',
							}}
						>
							<Stack spacing={1}>
								<Box
									display="flex"
									alignItems="center"
									justifyContent="space-between"
									gap={1}
								>
									<Typography variant="h6">
										{r.student?.first_name} {r.student?.last_name}
									</Typography>
									<Chip
										size="small"
										label={r.status}
										color={
											r.status === 'approved'
												? 'success'
												: r.status === 'rejected'
												? 'error'
												: 'warning'
										}
									/>
								</Box>
								<Typography variant="body2" color="text.secondary">
									Student {r.student?.username || 'Username unavailable'} ·{' '}
									{r.student?.email} · {r.student?.phone_number}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									Request: {r.reference}
								</Typography>
								<Paper variant="outlined" sx={{ p: 2, my: 1 }}>
									<Box
										component="dl"
										sx={{
											m: 0,
											display: 'grid',
											gridTemplateColumns: '1fr auto',
											gap: 1,
											'& dt, & dd': { m: 0 },
											'& dd': { fontWeight: 600, textAlign: 'right' },
										}}
									>
										<Typography component="dt" variant="body2">
											Wallet credit
										</Typography>
										<Typography component="dd" variant="body2">
											{r.amount} VC
										</Typography>
										<Typography component="dt" variant="body2">
											Fees
										</Typography>
										<Typography component="dd" variant="body2">
											{r.fee} EGP
										</Typography>
										<Typography component="dt" variant="body2" fontWeight={700}>
											Total transfer
										</Typography>
										<Typography component="dd" variant="body2">
											{r.total} EGP
										</Typography>
									</Box>
								</Paper>
								<Typography variant="body2">
									<strong>From:</strong> <bdi>{r.sending_number}</bdi>
								</Typography>
								<Typography variant="body2">
									<strong>Receiver:</strong> <bdi>{r.receiver_number}</bdi>
								</Typography>
								<Typography variant="body2">
									<strong>Submitted:</strong> {date(r.submitted_at)}
								</Typography>
								<Typography variant="body2">
									<strong>Created by:</strong>{' '}
									{r.source === 'admin'
										? `Admin #${r.created_by_admin_id}`
										: 'Student'}
								</Typography>
								{r.reviewed_at && (
									<Typography variant="body2">
										<strong>Reviewed:</strong> {date(r.reviewed_at)} by admin #
										{r.reviewed_by}
									</Typography>
								)}
								{r.rejection_note && (
									<Alert severity="error">{r.rejection_note}</Alert>
								)}
								{r.invoice_id && canViewInvoice && (
									<Button
										component={Link}
										to={`/dashboard/invoices/invoice/${r.invoice_id}`}
										sx={{ alignSelf: 'flex-start' }}
									>
										View Invoice
									</Button>
								)}
								{r.followup?.resolved_at && (
									<Alert severity="success">
										Follow-up resolved {date(r.followup.resolved_at)}:{' '}
										{r.followup.resolution_note}
									</Alert>
								)}
								{action && (
									<Paper variant="outlined" sx={{ p: 2 }}>
										<Typography variant="body2">
											{action === 'approve'
												? `Confirm credit of ${r.amount} VC to student ${
														r.student?.username || 'Username unavailable'
												  }. Verify the recipient, sender, transfer total and transaction have been checked.`
												: action === 'reject'
												? 'Explain why this request is rejected. The student will see this note.'
												: 'Record the contact outcome or resolution.'}
										</Typography>
										{action !== 'approve' && (
											<TextField
												fullWidth
												multiline
												required
												minRows={2}
												label={
													action === 'reject'
														? 'Rejection note'
														: 'Resolution note'
												}
												value={note}
												inputProps={{ maxLength: 2000 }}
												onChange={(e) => onNote(e.target.value)}
												sx={{ my: 2 }}
											/>
										)}
										<Typography variant="caption" color="text.secondary">
											{action === 'approve'
												? 'After approval, the next request will open automatically.'
												: ''}
										</Typography>
									</Paper>
								)}
								<Box
									component="details"
									sx={{
										pt: 1,
										'& summary': {
											cursor: 'pointer',
											fontWeight: 600,
											fontSize: '.875rem',
										},
									}}
								>
									<summary>Audit history ({r.events?.length || 0})</summary>
									{r.events?.map((e) => (
										<Typography
											key={e.id}
											variant="body2"
											sx={{ my: 1, whiteSpace: 'pre-wrap' }}
										>
											{date(e.created_at)} · {e.action} ·{' '}
											{e.actor_type === 'student'
												? `Student ${
														r.student?.username || 'Username unavailable'
												  }`
												: `${e.actor_type} #${e.actor_id}`}
											{e.note ? ` — ${e.note}` : ''}
										</Typography>
									))}
								</Box>
							</Stack>
						</Box>
						<Box
							sx={{
								display: 'grid',
								gridTemplateRows: 'auto minmax(0, 1fr)',
								gap: 1,
								p: 2,
								minHeight: 0,
								backgroundColor: 'grey.100',
								borderLeft: { md: '1px solid' },
								borderColor: { md: 'divider' },
							}}
						>
							<Typography variant="caption" color="text.secondary">
								Transaction proof · click image to open full size
							</Typography>
							<Box sx={{ minWidth: 0, minHeight: 0, height: '100%' }}>
								{proof || (
									<Typography variant="body2">No proof available.</Typography>
								)}
							</Box>
						</Box>
					</Box>
				)}
			</DialogContent>
			<DialogActions
				sx={{ p: 2, gap: 1, flexWrap: 'wrap', justifyContent: 'space-between' }}
			>
				<Stack direction="row" gap={1} flexWrap="wrap">
					{r &&
						(action ? (
							<>
								<Button
									variant="contained"
									disabled={busy || (action !== 'approve' && !note.trim())}
									onClick={onConfirm}
								>
									{busy ? 'Saving…' : `Confirm ${action}`}
								</Button>
								<Button disabled={busy} onClick={() => onAction('')}>
									Cancel
								</Button>
							</>
						) : (
							<>
								{r.status === 'pending' && canReview && (
									<>
										<Button
											variant="contained"
											disabled={busy}
											onClick={() => onAction('approve')}
										>
											Approve
										</Button>
										<Button
											color="error"
											variant="outlined"
											disabled={busy}
											onClick={() => onAction('reject')}
										>
											Reject
										</Button>
									</>
								)}
								{r.followup && !r.followup.resolved_at && canResolve && (
									<Button disabled={busy} onClick={() => onAction('resolve')}>
										Mark Contacted / Resolved
									</Button>
								)}
							</>
						))}
				</Stack>
				<Button disabled={busy} onClick={onClose}>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
}
